import { convertLegacyRouteRefs } from '@backstage/core-compat-api';
import {
  createComponentExtension,
  createPlugin,
  createRoutableExtension,
} from '@backstage/core-plugin-api';
import { CreateAppRouteBinder } from '@backstage/frontend-app-api';

import { createResourceLinkExtension } from '@spotify/confidence-core-react';
import { extractWorkflowFromInstanceName } from '@spotify/confidence-core-react';
import { createFeedItemExtension } from '@spotify/plugin-confidence-activity-react';
import {
  STRING_CHANGE_EXTENSION,
  WorkflowInstanceFeedItem,
  WorkflowInstanceResourceLink,
} from '@spotify/plugin-confidence-workflows-react';

import { ExternalRoutes, Routes, rootRouteRef } from './routes';

export const workflowsPlugin = createPlugin({
  id: 'workflows',
  routes: {
    ...Routes,
  },
  externalRoutes: {
    ...ExternalRoutes,
  },
});

export const bindWorkflowsRoutes = (
  bind: CreateAppRouteBinder,
  extraRoutes?: Parameters<typeof convertLegacyRouteRefs>[0],
) => {
  bind(
    convertLegacyRouteRefs(workflowsPlugin.externalRoutes),
    convertLegacyRouteRefs({
      ...workflowsPlugin.routes,
      ...extraRoutes,
    }),
  );
};

export const ConfidenceWorkflowsPage = workflowsPlugin.provide(
  createRoutableExtension({
    name: 'ConfidenceWorkflowsPage',
    component: () =>
      import(
        /* webpackChunkName: "confidence-workflows", webpackPrefetch: 10 */ './Router'
      ).then(m => m.Router),
    mountPoint: rootRouteRef,
  }),
);

export const ConfidenceWorkflowsExtension = workflowsPlugin.provide(
  createComponentExtension({
    name: 'ConfidenceWorkflowsExtension',
    component: {
      lazy: () =>
        import(
          /* webpackChunkName: "confidence-workflows-extension", webpackPrefetch: 10 */ './Router'
        ).then(m => m.Router),
    },
  }),
);

export const WorkflowInstanceResourceLinkExtension = workflowsPlugin.provide(
  createResourceLinkExtension({
    id: 'workflow-instance-resource-link',
    predicate: ({ name }) => /^workflows\/[^/]+\/instances\//.test(name),
    render: ({ name, text }) => (
      <WorkflowInstanceResourceLink name={name} text={text} />
    ),
  }),
);

// Feed item extensions

export const WorkflowInstanceFeedItemExtension = workflowsPlugin.provide(
  createFeedItemExtension({
    id: 'workflow-instance',
    predicate: ({ activity }) => {
      return (
        extractWorkflowFromInstanceName(activity.primaryResource) !==
          undefined &&
        activity.name.startsWith('activityTypes/workflow-instance-launched/')
      );
    },
    render: ({ activity }) => <WorkflowInstanceFeedItem activity={activity} />,
  }),
);

// traffic-validation

export const RecommendationChangedFeedItemExtension = workflowsPlugin.provide(
  createFeedItemExtension({
    id: 'recommendation-changed',
    predicate: ({ activity }) => {
      return activity.name.startsWith('activityTypes/recommendation-changed/');
    },
    render: () => null,
  }),
);

export const TrafficValidationFeedItemExtension = workflowsPlugin.provide(
  createFeedItemExtension({
    id: 'traffic-validation',
    predicate: ({ activity }) => {
      return activity.name.startsWith('activityTypes/traffic-validation/');
    },
    render: () => null,
  }),
);

export const MetricDeliveryFeedItemExtension = workflowsPlugin.provide(
  createFeedItemExtension({
    id: 'metric-delivery',
    predicate: ({ activity }) => {
      return activity.name.startsWith('activityTypes/metric-delivery/');
    },
    render: () => null,
  }),
);

export const SampleRatioMismatchFeedItemExtension = workflowsPlugin.provide(
  createFeedItemExtension({
    id: 'sample-ratio-mismatch',
    predicate: ({ activity }) => {
      return activity.name.startsWith('activityTypes/sample-ratio-mismatch/');
    },
    render: () => null,
  }),
);

// Change extensions

export const WorkflowStringChangeExtension = workflowsPlugin.provide(
  STRING_CHANGE_EXTENSION,
);
